import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import apiService from '../../Common/Configurations/APIService';
import config from '../../Common/Configurations/APIConfig';
import BrandedLoader from "../../Common/Layout/BrandedLoader";
import { useAuth } from "../../Common/Configurations/AuthContext";
import DefaultData from '../../CompanyData/DefaultData'; // Ensure this is imported
import { FaInfoCircle } from 'react-icons/fa';

const SightSeeingProducts = () => {
    const { state } = useLocation();
    const params = useParams();
    const navigate = useNavigate();
    const { user, loading } = useAuth();
    const [searchQuery, setSearchQuery] = useState({});
    const [pageLoading, setPageLoading] = useState(false);
    const [activities, setActivities] = useState([]);
    const [currency, setCurrency] = useState(DefaultData?.DEFAULTCURRENCY || "INR");
    const [exchangeRates, setExchangeRates] = useState([]);
    const [conversionFactor, setConversionFactor] = useState(1);
      const [showMarkup, setShowMarkup] = useState(true);
   
    const [filters, setFilters] = useState({
        ratings: [],
        priceRange: [100, 100000],
        durationRange: [0, 72000],
    });

    useEffect(() => {
        const getCurrencies = async () => {
            try {
                const response = await apiService.get(`${config.GETCURRENCY}`, 1000, 1);
                if (response && response.data) {
                    setExchangeRates(response.data);
                }
            } catch (error) {
                console.error("Error fetching currencies", error);
            }
        };
        getCurrencies();
    }, []);
     useEffect(() => {
            if (user?.currency && exchangeRates.length > 0) {
                const userCurrency = user.currency;
                setCurrency(userCurrency);
    
                if (userCurrency === "INR") {
                    setConversionFactor(1);
                } else {
                    const rateData = exchangeRates.find(
                        (r) => r.fromCurrency === "INR" && r.toCurrency === userCurrency
                    );
                    if (rateData) {
                        setConversionFactor(rateData.price);
                    } else {
                        setCurrency("INR");
                        setConversionFactor(1);
                    }
                }
            }
        }, [user, exchangeRates]);

    // --- 2. Handle Currency Change ---
    const handleCurrencyChange = (e) => {
        const selectedCode = e.target.value;
        setCurrency(selectedCode);
        if (selectedCode === "INR") {
            setConversionFactor(1);
        } else {
            const rateData = exchangeRates.find(r => r.fromCurrency === "INR" && r.toCurrency === selectedCode);
            setConversionFactor(rateData ? rateData.price : 1);
        }
    };
    
     const profileRate = useMemo(() => {
        return exchangeRates.find(r => r.toCurrency === user?.currency)?.price || 1;
    }, [exchangeRates, user]);

    const markupInINR = useMemo(() => {
        const rawMarkup = parseFloat(user?.sightseeingMarkup) || 0;
        return rawMarkup / profileRate;
    }, [user, profileRate]);

    // --- 3. Price Conversion Helper ---
  const calculatePrice = (priceInINR) => {
        if (!priceInINR) return 0;

        // Base INR + Normalized INR Markup
        const appliedMarkupINR = showMarkup ? markupInINR : 0;
        const totalInINR = parseFloat(priceInINR) + appliedMarkupINR;

        // Convert total to selected UI currency
        const finalPrice = totalInINR * conversionFactor;

        if (currency === "INR") {
            return Math.round(finalPrice).toLocaleString("en-IN");
        } else {
            return finalPrice.toFixed(2);
        }
    };


    const applyFilters = (activities) => {
        if (!Array.isArray(activities)) return [];
        return activities.filter((ac) => {
            const ratings = ac?.rating;
            const duration = ac.duration || 0;

            // 1. Calculate the TOTAL price in INR (Base + Markup)
            const appliedMarkupINR = showMarkup ? markupInINR : 0;
            const activePriceINR = parseFloat(ac.salePrice || 0) + appliedMarkupINR;

            // 2. Compare INR vs INR (Slider values are in INR)
            return (
                (filters.ratings.length === 0 || filters.ratings.includes(ratings)) &&
                duration >= filters.durationRange[0] &&
                duration <= filters.durationRange[1] &&
                activePriceINR >= filters.priceRange[0] && // Compare raw INR
                activePriceINR <= filters.priceRange[1]    // Compare raw INR
            );
        });
    };
    const handleFilterChange = (type, value) => {
        setFilters((prev) => {
            if (type === "ratings" || type === "duration") {
                const updated = prev[type].includes(value)
                    ? prev[type].filter((v) => v !== value)
                    : [...prev[type], value];
                return { ...prev, [type]: updated };
            } else if (type === "priceRange") {
                return { ...prev, priceRange: value };
            } else {
                return { ...prev, [type]: value };
            }
        });
    };
    const handleClearFilters = () => {
        setFilters({
            ratings: [],
            priceRange: [100, 100000],
            duration: [],
        });
    };
    function getTimeFromMins(mins) {
        var h = (mins / 60) | 0,
            m = mins % 60 | 0;
        var durationformat = h + "hrs " + m + "mins";
        return durationformat;
    }
    const BindPageData = (searchQuery) => {
        setPageLoading(true);
        GetProducts(searchQuery);
    };
    const GetProduct = (e, productQuery) => {
        e.preventDefault();
        navigate(
            "/sightseeing/product/" +
            productQuery.productId +
            "/" +
            params.destinationCode +
            "/" +
            params.destination +
            "/" +
            params.adults +
            "/" +
            params.kids +
            "/" +
            params.travelDate,
            { state: { searchQuery, productQuery, markupInINR: showMarkup ? markupInINR : 0  } }
        );
    };
    const GetProducts = async (searchQuery) => {
        const result = await apiService.post(
            config.SIGHTSEEINGPRODUCTS,
            searchQuery
        );
        if (result.succeeded) {
            setActivities(result.data.sightSeeingProductResponses);
        }
        setPageLoading(false);
    };
    useEffect(() => {
        setSearchQuery(state.searchQuery);
        BindPageData(state.searchQuery);
    }, []);
    if (loading) return <p>Loading...</p>;
    if (!user) {
        navigate("/");
    }
    return (
        <div className="container-fluid mt-3">
            <div className="card pt-0">
                <div className="card-body">
                    <div className="row">
                        <aside className="col-lg-3">
                            <div className="w-72 p-4 bg-gray-100 border-r">
                                <h3 className="text-lg font-bold mb-4">Filters</h3>

                                {/* --- CURRENCY DROPDOWN ADDED HERE --- */}
                                <div style={{ marginBottom: '20px' }}>
                                    <p className="text-muted text-uppercase fs-12 fw-medium mb-2">Currency</p>
                                    <select
                                            className="form-select form-select-sm"
                                            value={currency}
                                            onChange={handleCurrencyChange}
                                        >
                                            <option value="INR">INR</option>
                                            {exchangeRates.map((rate, index) => (
                                                // CHANGE: Use rate.toCurrency here, because that is the currency you want to convert TO
                                                <option key={index} value={rate.toCurrency}>
                                                    {rate.toCurrency}
                                                </option>
                                            ))}
                                        </select>
                                </div>

                                <div className={`p-3 border rounded mb-4 shadow-sm transition-all ${showMarkup ? 'bg-primary text-white' : 'bg-white text-dark'}`} 
                                     style={{ transition: '0.3s ease' }}>
                                    <div className="form-check form-switch mb-0">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="ssMarkupToggle"
                                            checked={showMarkup}
                                            onChange={(e) => setShowMarkup(e.target.checked)}
                                            style={{ cursor: 'pointer', borderColor: showMarkup ? '#fff' : '#ccc' }}
                                        />
                                        <label 
                                            className={`form-check-label fw-bold ${showMarkup ? 'text-white' : 'text-dark'}`} 
                                            htmlFor="ssMarkupToggle" 
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {showMarkup ? "+ Net" : " Net"}
                                        </label>
                                    </div>
                                    <div className={`mt-2 d-flex align-items-center ${showMarkup ? 'text-white-50' : 'text-success'}`}>
                                       
                                       
                                    </div>
                                </div>
                                {/* ------------------------------------ */}

                                {/* Rating Filter */}
                                <div style={{ marginTop: "10px" }}>
                                    <p className="text-muted text-uppercase fs-12 fw-medium mb-2">
                                        Ratings
                                    </p>
                                    {[5, 4, 3, 2, 1].map((rating) => (
                                        <div key={rating}>
                                            <label
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "8px",
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={filters.ratings.includes(rating)}
                                                    onChange={() =>
                                                        handleFilterChange("ratings", rating)
                                                    }
                                                />
                                                {"★".repeat(rating)}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                                <div style={{ marginTop: "10px" }}>
                                   <p className="text-muted text-uppercase fs-12 fw-medium mb-2">Price Range</p>
                                    <input type="range" className="form-range" min={200} max={100000} value={filters.priceRange[1]} onChange={(e) => handleFilterChange("priceRange", [200, parseInt(e.target.value)])} />
                                    <div className="fw-bold mt-1 text-primary small">Up to {currency} {calculatePrice(filters.priceRange[1])}</div>
                                </div>
                                <div style={{ marginTop: "10px" }}>
                                    <p className="text-muted text-uppercase fs-12 fw-medium mb-2">
                                        Duration Range
                                    </p>
                                    <input
                                        type="range"
                                        min={0}
                                        max={7200}
                                        value={filters.durationRange[1]}
                                        onChange={(e) =>
                                            handleFilterChange("duration", [
                                                0,
                                                parseInt(e.target.value),
                                            ])
                                        }
                                    />
                                    <div>Up to 24Hrs</div>
                                </div>
                            </div>
                        </aside>
                        <div className="col-lg-9 mt-4 mt-lg-0">
                            {pageLoading ? (
                                // Use our new centering wrapper
                                <div className="branded-loader-wrapper">
                                    <BrandedLoader />
                                </div>
                            ) : (
                                // When not loading, show the results
                                <div>
                                    <div className="border-bottom mb-3 pb-3">
                                        <div className="row align-items-center">
                                            <div className="col-6 col-md-8">
                                                {" "}
                                                <span className="me-3">
                                                    <span className="text-4">
                                                        {params.destination}:
                                                    </span>{" "}
                                                    <span className="fw-600">
                                                        {activities.length} tourist places
                                                    </span>{" "}
                                                    found
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="hotels-list">


                                        {applyFilters(activities).map((a) => (
                                            <div className="hotels-item bg-white shadow-md rounded p-3">
                                                <div className="row">
                                                    <div className="col-md-4">
                                                        <Link to={a.imageURL} target="_blank">
                                                            <img
                                                                className="img-fluid rounded align-top"
                                                                src={a.imageURL}
                                                                alt={a.productName}
                                                            />
                                                        </Link>{" "}
                                                    </div>
                                                    <div className="col-md-8 ps-3 ps-md-0 mt-3 mt-md-0">
                                                        <div className="row g-0">
                                                            <div className="col-sm-9">
                                                                <h4 className="text-dark text-5">
                                                                    {a.productName}
                                                                </h4>
                                                                <p>
                                                                    {Array.from({ length: 5 }, (_, i) => (
                                                                        <span
                                                                            className="text-warning fs-5"
                                                                            key={i}
                                                                        >
                                                                            {i < a.rating ? "★" : "☆"}
                                                                        </span>
                                                                    ))}
                                                                </p>
                                                                {a.duration !== 0 ? (
                                                                    <p className="mb-1">
                                                                        Duration : {getTimeFromMins(a.duration)}
                                                                    </p>
                                                                ) : (
                                                                    ""
                                                                )}
                                                                <p className="mb-1">{a.destinationName}</p>
                                                                <p className="mb-1">
                                                                    {a.description.length > 100
                                                                        ? `${a.description.substring(
                                                                            0,
                                                                            100
                                                                        )}...`
                                                                        : a.description}
                                                                </p>
                                                            </div>
                                                            <div className="col-sm-3 text-end d-flex d-sm-block align-items-center">
                                                                 <div className="text-dark fs-5 fw-bold mb-2">
                                                                    {currency} {calculatePrice(a.salePrice)}
                                                                </div>
                                                                <button
                                                                    onClick={(e) => GetProduct(e, a)}
                                                                    className="btn btn-md btn-primary full-width fw-medium px-lg-4"
                                                                >
                                                                    View Details
                                                                    <i className="fa-solid fa-arrow-trend-up ms-2" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SightSeeingProducts;